import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Alert,
    AlertIcon,
    Box,
    Button,
    Checkbox,
    Flex,
    HStack,
    Icon,
    IconButton,
    Link as ChakraLink,
    Spinner,
    Table,
    Tag,
    Tbody,
    Td,
    Text,
    Thead,
    Tooltip,
    Tr,
    useToast,
    VStack,
} from '@chakra-ui/react';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    TbArrowBackUp,
    TbArrowDown,
    TbArrowUp,
    TbBuilding,
    TbCircleCheck,
    TbCircleX,
    TbClipboardCheck,
    TbEditCircle,
    TbLayoutBoardSplit,
    TbLayoutList,
    TbPackageImport,
} from 'react-icons/tb';
import { formatPercentage, formatPhoneNumber, formatPrice } from '../functions';
import CollapsibleSearch, { LIST_PAGE_SEARCH_WIDTH } from '../components/CollapsibleSearch';
import CopyButton from '../components/CopyButton';
import LocationSelector from '../components/LocationSelector';
import Pagination from '../components/Pagination';
import RefreshButton from '../components/RefreshButton';
import ListTabRow from '../components/ListTabRow';
import BulkActionBar, { type BulkActionBarItem } from '../components/BulkActionBar';
import ResizableTh from '../components/ResizableTh';
import DeliveryBoardEtaFilter from '../components/DeliveryBoardEtaFilter';
import DeliveryBoardKanban from '../components/DeliveryBoardKanban';
import { useQueryClient } from '@tanstack/react-query';
import MarkReceivedModal, { MarkReceivedOrderTab } from '../modals/MarkReceivedModal';
import OrderDrawer from '../modals/OrderDrawer';
import SessionContext from '../contexts/SessionContext';
import { canMarkPartsReceived, isDeliveryRowReceivable } from '../utils/deliveryReceiveAccess';
import { useColumnWidths } from '../hooks/useColumnWidths';
import { useGetLocations } from '../hooks/useGetLocations';
import { useReceiveParts } from '../hooks/useReceiveParts';
import { useResponsiveToolbar } from '../hooks/useResponsiveToolbar';
import usePageTitle from '../hooks/usePageTitle';
import { useScrollShadow } from '../hooks/useScrollShadow';
import { useTableSettings } from '../hooks/useTableSettings';
import {
    useDeliveryBoardFeed,
    DELIVERY_BOARD_FEED_PATH,
    DeliveryBoardFilter,
    DeliveryBoardGrouping,
    DeliveryBoardRangeList,
    DeliveryBoardRow,
    DeliveryBoardSortKey,
} from '../hooks/useDeliveryBoardFeed';
import { DELIVERY_BOARD_PATH, useDeliveryBoardData } from '../hooks/useDeliveryBoardData';
import {
    DEFAULT_DELIVERY_BOARD_ETA_FILTERS,
    DeliveryBoardColumnKey,
    DeliveryBoardColumnPreset,
    useDeliveryBoardPreferences,
} from '../hooks/useDeliveryBoardPreferences';
import ReportingTableSettingsDrawer from './reporting/components/ReportingTableSettingsDrawer';

/** Matches the Td vertical padding used by Home/Dashboard/RO Manager tables. */
const CELL_PY = 3;
const compactTagProps = {
    size: 'sm' as const,
    variant: 'subtle' as const,
    borderRadius: 'md' as const,
    fontSize: 'xs' as const,
    h: '20px',
    minH: '20px',
    py: 0,
    display: 'inline-flex' as const,
    alignItems: 'center' as const,
    flexShrink: 0,
};

type DisplayRow =
    | { type: 'group'; key: string; label: string; count: number; rows: DeliveryBoardRow[]; }
    /** `index` is the row's position in display order (see `listRange`). */
    | { type: 'row'; row: DeliveryBoardRow; index: number; };

const FILTER_OPTIONS: { value: DeliveryBoardFilter; label: string; icon: React.ElementType; }[] = [
    { value: 'active', label: 'Active', icon: TbCircleCheck as React.ElementType },
    { value: 'part-changes', label: 'ETA Changes', icon: TbEditCircle as React.ElementType },
    { value: 'backordered', label: 'Backordered', icon: TbPackageImport as React.ElementType },
    { value: 'unfulfilled', label: 'Unfulfilled', icon: TbCircleX as React.ElementType },
    { value: 'received', label: 'Parts Marked Received', icon: TbClipboardCheck as React.ElementType },
];

const DEFAULT_FILTER: DeliveryBoardFilter = 'active';
const MARK_RECEIVED_ACTIONS: BulkActionBarItem[] = [
    { id: 'mark-received', label: 'Mark Received' },
];
const FILTER_STORAGE_KEY = 'delivery_board_filter';

/** Left padding of the first column reserving a gutter for the hover checkbox. */
const FIRST_COL_PL = '44px';

const formatShortDate = (date: Date) => {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = String(date.getFullYear()).slice(-2);
    return `${month}/${day}/${year}`;
};

const formatTimeOfDay = (date: Date) => date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
});

/** Matches Home/Dashboard OrderHoverCheckbox — default Chakra blue when checked. */
const receiveCheckboxSx = {
    '& .chakra-checkbox__control': {
        width: '14px',
        height: '14px',
    },
    '& .chakra-checkbox__icon': {
        fontSize: '9px',
    },
};

const DeliveryBoard = () => {
    const { data: locationsData } = useGetLocations();
    const { settings, updateSettings, tableSettingsSx } = useTableSettings('delivery-board');
    const { widths: colWidths, updateWidth: updateColWidth, resetWidths: resetColWidths } = useColumnWidths('delivery-board');
    const [selectedFilter, setSelectedFilter] = useState<DeliveryBoardFilter>(() => {
        try {
            const stored = JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY) ?? 'null');
            const isValid = FILTER_OPTIONS.some((option) => option.value === stored);
            return isValid ? stored as DeliveryBoardFilter : DEFAULT_FILTER;
        } catch {
            return DEFAULT_FILTER;
        }
    });
    /**
     * "Parts Marked Received" is an audit trail, not a working column: rows
     * aren't selectable, ETA windows don't apply (server sorts by receivedAt
     * desc), and each row shows who received it and when, with an undo.
     */
    const isAuditTab = selectedFilter === 'received';
    const columnPreset: DeliveryBoardColumnPreset = 'default';
    const {
        columnRows,
        visibleColumnKeys,
        toggleColumn,
        reorderColumns,
        resetColumns,
        view,
        setView,
        grouping,
        setGrouping,
        sort,
        setSort,
        etaFilters,
        setEtaFilters,
    } = useDeliveryBoardPreferences(columnPreset);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { showRightShadow } = useScrollShadow(scrollContainerRef);
    const { containerRef: toolbarRef, locationCompact, searchCompact } = useResponsiveToolbar();
    usePageTitle('Delivery Board');
    const toast = useToast();
    // Opt out of board invalidation: just-received rows stay visible locally
    // (struck through, undoable) until the next natural refetch.
    const receiveMutation = useReceiveParts({ invalidateBoard: false });
    const session = useContext(SessionContext);
    // Board route/nav is gated on opt-in; this stays true while the page is mounted.
    const orgCanReceive = canMarkPartsReceived(session);

    const [locationIds, setLocationIds] = useState<Array<string> | 'all'>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [isMarkReceivedOpen, setIsMarkReceivedOpen] = useState(false);

    // Receive workflow state. Selected rows open the blue bar; Mark Received
    // opens a multi-RO modal. Keep full row metadata so tabs survive pagination.
    // Just-received rows stay visible (struck through, undoable) until the next
    // refetch drops them. Shift+click range-select mirrors Home/Dashboard.
    const [selectedById, setSelectedById] = useState<Map<string, DeliveryBoardRow>>(() => new Map());
    const selectedIds = useMemo(() => new Set(selectedById.keys()), [selectedById]);
    const [receivedIds, setReceivedIds] = useState<Set<string>>(() => new Set());
    /** Deep-link target for the order drawer: expand the row's supplier order and highlight its part. */
    const [drawerTarget, setDrawerTarget] = useState<{
        orderId: string;
        supplierOrderId: string | null;
        orderPartId: string;
    } | null>(null);
    /**
     * Shift-select anchor, scoped to the list it was set in — a row index is
     * only meaningful against its own list, so an anchor from another kanban
     * column or a previous page is ignored rather than applied to the wrong rows.
     */
    const lastClickedRef = useRef<{ listId: string; index: number } | null>(null);
    const shiftKeyRef = useRef(false);

    // Match Home/Dashboard density visually: single-line rows are shorter than
    // Home's vehicle+VIN stacks, so load enough rows to fill the viewport.
    // The hook over-fetches one extra row internally to answer hasNextPage.
    const limit = 40;
    const selectedFilters = useMemo<DeliveryBoardFilter[]>(() => [selectedFilter], [selectedFilter]);

    useEffect(() => {
        localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(selectedFilter));
    }, [selectedFilter]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setCurrentPage(1);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        setCurrentPage(1);
    }, [grouping, view, etaFilters]);

    useEffect(() => {
        setSelectedById(new Map());
        // Also drop the shift-select anchor: listRange.id only encodes
        // grouping + page, so after a search/filter/location/sort change the
        // anchor index would otherwise resolve against a completely different
        // row set (a re-sorted page reorders the same rows under the same id).
        lastClickedRef.current = null;
    }, [selectedFilter, debouncedSearch, view, currentPage, locationIds, etaFilters, sort]);

    const {
        data,
        isLoading,
        isError,
        isRefetching,
        refetch,
    } = useDeliveryBoardFeed(
        locationIds,
        currentPage,
        limit,
        selectedFilters,
        // ETA windows don't apply to the received audit trail (the control is
        // hidden there); the API ignores them for `received` but don't send a
        // stale persisted window regardless.
        isAuditTab ? [] : etaFilters,
        sort,
        grouping,
        debouncedSearch,
        { enabled: view === 'list' }
    );

    const boardData = useDeliveryBoardData({
        locationIds,
        etaFilters,
        sort,
        search: debouncedSearch,
        enabled: view === 'board',
    });

    const refreshAll = useCallback(() => {
        if (view === 'board') {
            boardData.refetchAll();
        } else {
            void refetch();
        }
    }, [view, boardData, refetch]);

    const queryClient = useQueryClient();
    /**
     * Receive/undo keeps the currently displayed rows local (struck through,
     * undoable — useReceiveParts is constructed with invalidateBoard: false),
     * but every OTHER cached board query is stale the moment the mutation
     * lands: the received audit trail is missing a just-received part, a
     * working tab is missing an undone one. Invalidate every board cache
     * entry except the view/tab on screen, so siblings refetch when opened
     * while the current view keeps its optimistic rows.
     */
    const invalidateSiblingBoardQueries = useCallback(() => {
        void queryClient.invalidateQueries({
            predicate: (query) => {
                const key = query.queryKey;
                if (key[0] === DELIVERY_BOARD_PATH) {
                    // Kanban cache: stale unless it's the view on screen.
                    return view !== 'board';
                }
                if (key[0] !== DELIVERY_BOARD_FEED_PATH) return false;
                if (view === 'board') return true;
                // List feed key: [path, locations, page, limit, filters, ...]
                return key[4] !== selectedFilter;
            },
        });
    }, [queryClient, view, selectedFilter]);

    const isAnyRefetching = view === 'board' ? boardData.isAnyFetching : isRefetching;

    const rows = useMemo(() => {
        const fetched = data?.rows ?? [];
        return {
            rows: fetched.slice(0, limit),
            hasNextPage: fetched.length > limit,
        };
    }, [data?.rows, limit]);

    const totalPages = useMemo(() => (
        rows.hasNextPage ? currentPage + 1 : currentPage
    ), [rows.hasNextPage, currentPage]);

    const showLocationSelector = useMemo(() => {
        const myLocations = locationsData?.myLocations ?? [];
        if (myLocations.length > 0) return myLocations.length > 1;
        const filteredLocations = (locationsData?.locations ?? []).filter((location) => location._id !== 'all');
        return filteredLocations.length > 1;
    }, [locationsData]);

    useEffect(() => {
        if (showLocationSelector) return;
        if (locationIds === 'all' || locationIds.length > 0) return;
        if (!locationsData) return;

        if (locationsData.myLocations && locationsData.myLocations.length > 0) {
            setLocationIds(locationsData.myLocations);
            return;
        }

        if (locationsData.locations.some((location) => location._id === 'all')) {
            setLocationIds('all');
            return;
        }

        const firstLocation = locationsData.locations[0];
        if (firstLocation) {
            setLocationIds([firstLocation._id]);
        }
    }, [locationIds, locationsData, showLocationSelector]);

    const handleLocationChange = useCallback((nextLocationIds: Array<string> | 'all') => {
        setLocationIds(nextLocationIds);
        setCurrentPage(1);
    }, []);

    /* ── Receive workflow ─────────────────────────────────────────── */

    const isReceivable = useCallback((row: DeliveryBoardRow) => (
        isDeliveryRowReceivable(session, row) && !receivedIds.has(row._id)
    ), [session, receivedIds]);

    const displayRows = useMemo<DisplayRow[]>(() => {
        if (grouping === 'none') {
            return rows.rows.map((row, index) => ({ type: 'row', row, index }));
        }

        const grouped = new Map<string, DeliveryBoardRow[]>();
        const labels = new Map<string, string>();

        rows.rows.forEach((row) => {
            // Supplier grouping clusters parts by vendor across every RO so the same
            // supplier's inbound parts sit together. Parts not yet placed with a supplier
            // fall into a single "No Supplier" bucket.
            const key = grouping === 'ro'
                ? `ro-${row.roNumber}-${row.orderId}`
                : row.vendorName
                    ? `supplier-${row.vendorName.trim().toLowerCase()}`
                    : 'supplier-unassigned';
            const label = grouping === 'ro'
                ? `RO ${row.roNumber}`
                : (row.vendorName ?? 'No Supplier');

            const current = grouped.get(key) ?? [];
            current.push(row);
            grouped.set(key, current);
            labels.set(key, label);
        });

        // Indices count rendered rows in display order — grouping re-buckets the
        // fetched page, so fetched order can't be used for shift-select ranges.
        let index = 0;
        return Array.from(grouped.entries()).flatMap(([key, groupRows]): DisplayRow[] => ([
            { type: 'group' as const, key, label: labels.get(key) ?? key, count: groupRows.length, rows: groupRows },
            ...groupRows.map((row) => ({ type: 'row' as const, row, index: index++ })),
        ]));
    }, [grouping, rows.rows]);

    /**
     * The list view's rows, flattened in the exact order they render. Shift
     * ranges index into this list, so its identity carries the grouping mode
     * and page — an anchor set under one arrangement must not extend a range
     * under another.
     */
    const listRange = useMemo<DeliveryBoardRangeList>(() => ({
        id: `list:${grouping}:${currentPage}`,
        rows: displayRows.flatMap((entry) => (entry.type === 'row' ? [entry.row] : [])),
    }), [currentPage, displayRows, grouping]);

    /**
     * Toggle one row, or shift+click a range. `range` is the ordered list the
     * index applies to (defaults to the list view's display order; kanban
     * passes its column). Clicking a row/card, not just the checkbox, drives
     * the blue bar.
     */
    const toggleSelected = useCallback((
        row: DeliveryBoardRow,
        rowIndex: number,
        shiftKey: boolean,
        range?: DeliveryBoardRangeList,
    ) => {
        if (!orgCanReceive) return;

        const listId = range?.id ?? listRange.id;
        const rangeRows = range?.rows ?? listRange.rows;
        const anchor = lastClickedRef.current;
        // An anchor only applies inside the list it was set in; shift+clicking
        // in a different column or page starts a fresh one instead.
        const anchorIndex = shiftKey && anchor?.listId === listId ? anchor.index : null;

        if (!isReceivable(row) && anchorIndex === null) return;
        setSelectedById((prev) => {
            const next = new Map(prev);

            if (anchorIndex !== null) {
                const start = Math.min(anchorIndex, rowIndex);
                const end = Math.max(anchorIndex, rowIndex);
                for (let i = start; i <= end; i++) {
                    const candidate = rangeRows[i];
                    if (candidate && isReceivable(candidate)) next.set(candidate._id, candidate);
                }
            } else if (!isReceivable(row)) {
                return prev;
            } else if (next.has(row._id)) {
                next.delete(row._id);
            } else {
                next.set(row._id, row);
            }

            return next;
        });
        lastClickedRef.current = { listId, index: rowIndex };
    }, [isReceivable, listRange, orgCanReceive]);

    const toggleGroupSelected = useCallback((groupRows: DeliveryBoardRow[]) => {
        const receivable = groupRows.filter(isReceivable);
        if (receivable.length === 0) return;
        setSelectedById((prev) => {
            const next = new Map(prev);
            const allSelected = receivable.every((row) => next.has(row._id));
            receivable.forEach((row) => {
                if (allSelected) next.delete(row._id);
                else next.set(row._id, row);
            });
            return next;
        });
    }, [isReceivable]);

    const clearSelection = useCallback(() => {
        setSelectedById(new Map());
        lastClickedRef.current = null;
    }, []);

    const markReceivedOrders = useMemo<MarkReceivedOrderTab[]>(() => {
        const byOrder = new Map<string, MarkReceivedOrderTab>();
        selectedById.forEach((row) => {
            const existing = byOrder.get(row.orderId);
            if (existing) {
                existing.initialPartIds.push(row._id);
                return;
            }
            byOrder.set(row.orderId, {
                orderId: row.orderId,
                roNumber: row.roNumber,
                vehicle: row.vehicle,
                initialPartIds: [row._id],
            });
        });
        return Array.from(byOrder.values());
    }, [selectedById]);

    const openMarkReceivedModal = useCallback(() => {
        if (selectedById.size === 0) return;
        setIsMarkReceivedOpen(true);
    }, [selectedById.size]);

    const onMarkReceivedSuccess = useCallback((orderPartIds: string[], updatedCount: number) => {
        clearSelection();
        invalidateSiblingBoardQueries();
        if (updatedCount < orderPartIds.length) {
            // The server skipped some parts (orders predating the receiving
            // opt-in). We don't know which ids were recorded, so settle to
            // server truth instead of striking through unrecorded rows.
            refreshAll();
            const skipped = orderPartIds.length - updatedCount;
            toast({
                title: `${updatedCount} of ${orderPartIds.length} parts marked received`,
                description: `${skipped} part${skipped === 1 ? ' was' : 's were'} skipped (not eligible for receiving).`,
                status: 'warning',
                duration: 5000,
                isClosable: true,
            });
            return;
        }
        setReceivedIds((prev) => {
            const next = new Set(prev);
            orderPartIds.forEach((id) => next.add(id));
            return next;
        });
        toast({
            title: `${updatedCount} part${updatedCount === 1 ? '' : 's'} marked received`,
            status: 'success',
            duration: 2500,
            isClosable: true,
        });
    }, [clearSelection, invalidateSiblingBoardQueries, refreshAll, toast]);

    const onUndoReceive = useCallback((row: DeliveryBoardRow) => {
        receiveMutation.mutate({ orderPartIds: [row._id], received: false }, {
            onSuccess: () => {
                setReceivedIds((prev) => {
                    const next = new Set(prev);
                    next.delete(row._id);
                    return next;
                });
                invalidateSiblingBoardQueries();
            },
            onError: () => {
                toast({
                    title: 'Failed to undo receive',
                    description: `${row.description} is still marked received.`,
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                });
            },
        });
    }, [invalidateSiblingBoardQueries, receiveMutation, toast]);

    /**
     * Audit-tab undo: clear the receive flag, then refetch so the row leaves
     * the trail (and reappears on its working column). A part received
     * earlier this visit also has an optimistic receivedIds entry — drop it
     * so the row doesn't come back struck-through on the working tabs.
     */
    const onUndoFromAudit = useCallback((row: DeliveryBoardRow) => {
        receiveMutation.mutate({ orderPartIds: [row._id], received: false }, {
            onSuccess: () => {
                setReceivedIds((prev) => {
                    if (!prev.has(row._id)) return prev;
                    const next = new Set(prev);
                    next.delete(row._id);
                    return next;
                });
                toast({
                    title: 'Receive undone',
                    description: `${row.description} is back on the board.`,
                    status: 'success',
                    duration: 2500,
                    isClosable: true,
                });
                // Refetch the audit list so the row leaves the trail, and
                // invalidate the working tabs so it reappears there.
                void refetch();
                invalidateSiblingBoardQueries();
            },
            onError: () => {
                toast({
                    title: 'Failed to undo receive',
                    description: `${row.description} is still marked received.`,
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                });
            },
        });
    }, [invalidateSiblingBoardQueries, receiveMutation, refetch, toast]);

    const updateSortPreference = useCallback((key: DeliveryBoardSortKey, dir: 'asc' | 'desc') => {
        setSort({ key, dir });
        setCurrentPage(1);
    }, [setSort]);

    const columnDefinitions = useMemo(() => {
        const definitions: Record<DeliveryBoardColumnKey, {
            label: string;
            header: string;
            minWidth: number;
            isNumeric?: boolean;
            render: (row: DeliveryBoardRow) => React.ReactNode;
        }> = {
            roNumber: {
                label: 'RO Number',
                header: 'RO Number',
                minWidth: 120,
                render: (row) => (
                    <ChakraLink
                        color="blue.600"
                        fontWeight="medium"
                        fontSize="sm"
                        _hover={{ textDecoration: 'underline' }}
                        onClick={(event) => {
                            event.stopPropagation();
                            // Open the order drawer deep-linked to this row's
                            // supplier order + part (the full order page stays
                            // reachable from the drawer header).
                            setDrawerTarget({
                                orderId: row.orderId,
                                supplierOrderId: row.supplierOrderId,
                                orderPartId: row._id,
                            });
                        }}
                    >
                        {row.roNumber}
                    </ChakraLink>
                ),
            },
            customerName: {
                label: 'Customer',
                header: 'Customer',
                minWidth: 160,
                render: (row) => (
                    <Text fontSize="sm" color="gray.700" isTruncated>
                        {row.customerName ?? '—'}
                    </Text>
                ),
            },
            vehicle: {
                label: 'Vehicle',
                header: 'Vehicle',
                minWidth: 180,
                render: (row) => (
                    <Text fontSize="sm" color="gray.700" isTruncated>
                        {row.vehicle}
                    </Text>
                ),
            },
            vin: {
                label: 'VIN',
                header: 'VIN',
                minWidth: 140,
                render: (row) => (
                    row.vin ? (
                        <CopyButton
                            text={row.vin}
                            label="Copy VIN"
                            tooltipAnchor={
                                <Text fontSize="sm" color="gray.700" fontFamily="mono">
                                    {row.vin}
                                </Text>
                            }
                            _hover={{ color: 'gray.600', bg: 'gray.100' }}
                            minW="auto"
                            h="auto"
                            p={1}
                        />
                    ) : (
                        <Text fontSize="sm" color="gray.700">—</Text>
                    )
                ),
            },
            timeStarted: {
                label: 'Time Started',
                header: 'Time Started',
                minWidth: 140,
                render: (row) => (
                    row.timeStarted ? (
                        <Text fontSize="sm" color="gray.700" whiteSpace="nowrap">
                            {formatShortDate(row.timeStarted)} {formatTimeOfDay(row.timeStarted)}
                        </Text>
                    ) : <Text fontSize="sm" color="gray.700">—</Text>
                ),
            },
            timePlaced: {
                label: 'Time Placed',
                header: 'Time Placed',
                minWidth: 140,
                render: (row) => (
                    row.timePlaced ? (
                        <Text fontSize="sm" color="gray.700" whiteSpace="nowrap">
                            {formatShortDate(row.timePlaced)} {formatTimeOfDay(row.timePlaced)}
                        </Text>
                    ) : <Text fontSize="sm" color="gray.700">—</Text>
                ),
            },
            description: {
                label: 'Part Name',
                header: 'Part Name',
                minWidth: 220,
                render: (row) => {
                    // Optimistic just-received styling only applies on the
                    // working tabs; on the audit tab every row is received.
                    const optimisticReceived = !isAuditTab && receivedIds.has(row._id);
                    return (
                    <HStack spacing={2} align="center" minW={0}>
                        <Text
                            fontSize="sm"
                            fontWeight="medium"
                            color={optimisticReceived ? 'gray.500' : 'gray.800'}
                            textDecoration={optimisticReceived ? 'line-through' : undefined}
                            isTruncated
                        >
                            {row.description}
                        </Text>
                        {optimisticReceived && (
                            <Tag {...compactTagProps} colorScheme="green">
                                Received
                            </Tag>
                        )}
                        {row.state === 'unfulfilled' && row.action && (
                            <Tag
                                {...compactTagProps}
                                colorScheme={row.action === 'cancellation' ? 'red' : 'orange'}
                            >
                                {row.action === 'cancellation' ? 'Cancelled' : 'Returned'}
                            </Tag>
                        )}
                    </HStack>
                    );
                },
            },
            partNumber: {
                label: 'Part Number',
                header: 'Part Number',
                minWidth: 170,
                render: (row) => (
                    row.partNumber ? (
                        <CopyButton
                            text={row.partNumber}
                            label="Copy part number"
                            tooltipAnchor={
                                <Text fontSize="sm" color="gray.700">
                                    {row.partNumber}
                                </Text>
                            }
                            _hover={{ color: 'gray.600', bg: 'gray.100' }}
                            minW="auto"
                            h="auto"
                            p={1}
                        />
                    ) : <Text fontSize="sm" color="gray.700">—</Text>
                ),
            },
            vendorName: {
                label: 'Vendor Name',
                header: 'Vendor',
                minWidth: 180,
                render: (row) => (
                    <Text fontSize="sm" color="gray.700" isTruncated>
                        {row.vendorName ?? '—'}
                    </Text>
                ),
            },
            eta: {
                label: 'ETA',
                header: 'ETA',
                minWidth: 110,
                render: (row) => (
                    row.originalEta ? (
                        <HStack align="center" spacing={1.5} whiteSpace="nowrap">
                            <Text fontSize="sm" color="gray.500" textDecoration="line-through">
                                {row.originalEta.label}
                            </Text>
                            <Text fontSize="sm" fontWeight="medium" color="orange.500">
                                {row.eta.label}
                            </Text>
                        </HStack>
                    ) : (
                        <Text fontSize="sm" fontWeight="medium" color="gray.800" whiteSpace="nowrap">
                            {row.eta.label}
                        </Text>
                    )
                ),
            },
            listPrice: {
                label: 'List Price',
                header: 'List Price',
                minWidth: 120,
                isNumeric: true,
                render: (row) => (
                    <Text fontSize="sm" color="gray.700">
                        {row.listPrice != null ? formatPrice(row.listPrice) : '—'}
                    </Text>
                ),
            },
            buyPrice: {
                label: 'Buy Price',
                header: 'Buy Price',
                minWidth: 120,
                isNumeric: true,
                render: (row) => (
                    <Text fontSize="sm" color="gray.700">
                        {row.buyPrice != null ? formatPrice(row.buyPrice) : '—'}
                    </Text>
                ),
            },
            grossMargin: {
                label: 'Gross Margin',
                header: 'Gross Margin',
                minWidth: 120,
                isNumeric: true,
                render: (row) => (
                    <Text fontSize="sm" color="gray.700">
                        {row.grossMargin != null ? formatPercentage(row.grossMargin) : '—'}
                    </Text>
                ),
            },
            vendorPhone: {
                label: 'Phone Number',
                header: 'Phone Number',
                minWidth: 170,
                render: (row) => (
                    row.vendorPhone ? (
                        <CopyButton
                            text={row.vendorPhone}
                            label="Copy phone number"
                            tooltipAnchor={
                                <Text fontSize="sm" color="gray.700">
                                    {formatPhoneNumber(row.vendorPhone)}
                                </Text>
                            }
                            _hover={{ color: 'gray.600', bg: 'gray.100' }}
                            minW="auto"
                            h="auto"
                            p={1}
                        />
                    ) : <Text fontSize="sm" color="gray.700">—</Text>
                ),
            },
            vendorEmail: {
                label: 'Email',
                header: 'Email',
                minWidth: 240,
                render: (row) => (
                    row.vendorEmail ? (
                        <CopyButton
                            text={row.vendorEmail}
                            label="Copy email"
                            tooltipAnchor={
                                <Text fontSize="sm" color="gray.700" isTruncated>
                                    {row.vendorEmail}
                                </Text>
                            }
                            _hover={{ color: 'gray.600', bg: 'gray.100' }}
                            minW="auto"
                            h="auto"
                            p={1}
                        />
                    ) : <Text fontSize="sm" color="gray.700">—</Text>
                ),
            },
        };

        const columns: Array<{
            key: string;
            label: string;
            header: string;
            minWidth: number;
            isNumeric?: boolean;
            render: (row: DeliveryBoardRow) => React.ReactNode;
        }> = visibleColumnKeys.map((key) => ({
            key,
            ...definitions[key],
        }));

        // The audit trail's defining column — always present on that tab,
        // independent of the user's configurable column set.
        if (isAuditTab) {
            columns.push({
                key: 'received',
                label: 'Received',
                header: 'Received',
                minWidth: 220,
                render: (row) => {
                    const receivedAt = row.receivedAt ? new Date(row.receivedAt) : null;
                    return (
                        <HStack spacing={3} align="center" whiteSpace="nowrap">
                            <Box>
                                <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                    {receivedAt ? `${formatShortDate(receivedAt)} ${formatTimeOfDay(receivedAt)}` : '—'}
                                </Text>
                                {row.receivedByName && (
                                    <Text fontSize="xs" color="gray.500">
                                        by {row.receivedByName}
                                    </Text>
                                )}
                            </Box>
                            {orgCanReceive && (
                                <Tooltip label="Undo received" placement="top" hasArrow>
                                    <IconButton
                                        aria-label="Undo received"
                                        icon={<Icon as={TbArrowBackUp as React.ElementType} boxSize={4} />}
                                        size="xs"
                                        variant="ghost"
                                        color="gray.500"
                                        _hover={{ color: 'gray.700', bg: 'gray.100' }}
                                        isDisabled={receiveMutation.isPending}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onUndoFromAudit(row);
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </HStack>
                    );
                },
            });
        }

        return columns;
    }, [visibleColumnKeys, receivedIds, isAuditTab, orgCanReceive, onUndoFromAudit, receiveMutation.isPending]);

    const settingsExtraTabs = useMemo(() => [
        {
            key: 'preferences',
            label: 'Preferences',
            content: (
                <Accordion allowMultiple defaultIndex={[0, 1, 2]}>
                    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="visible" mb={3}>
                        <AccordionItem border="none">
                            <AccordionButton px={4} py={3} _hover={{ bg: 'transparent' }}>
                                <Text flex={1} textAlign="left" fontWeight="semibold" fontSize="md" color="gray.800">
                                    View
                                </Text>
                                <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel px={4} pb={4} pt={1}>
                                <VStack align="stretch" spacing={3}>
                                    {[
                                        {
                                            value: 'list' as const,
                                            label: 'List',
                                            description: 'Standard table view for dense scanning.',
                                            icon: TbLayoutList as React.ElementType,
                                        },
                                        {
                                            value: 'board' as const,
                                            label: 'Board',
                                            description: 'Lifecycle columns for quick triage by urgency.',
                                            icon: TbLayoutBoardSplit as React.ElementType,
                                        },
                                    ].map((option) => {
                                        const isSelected = view === option.value;
                                        return (
                                            <Box
                                                key={option.value}
                                                as="button"
                                                type="button"
                                                onClick={() => setView(option.value)}
                                                borderWidth="1px"
                                                borderColor={isSelected ? '#0071E3' : 'gray.200'}
                                                borderRadius="lg"
                                                px={4}
                                                py={3}
                                                bg={isSelected ? '#E6F1FD' : 'white'}
                                                textAlign="left"
                                                _hover={{ borderColor: isSelected ? '#0071E3' : 'gray.300', bg: isSelected ? '#E6F1FD' : 'gray.50' }}
                                            >
                                                <HStack spacing={3}>
                                                    <Icon as={option.icon} boxSize={5} color={isSelected ? '#0071E3' : 'gray.500'} />
                                                    <Box>
                                                        <Text fontSize="sm" fontWeight="semibold" color={isSelected ? '#0071E3' : 'gray.800'}>
                                                            {option.label}
                                                        </Text>
                                                        <Text fontSize="xs" color="gray.500" mt={1}>
                                                            {option.description}
                                                        </Text>
                                                    </Box>
                                                </HStack>
                                            </Box>
                                        );
                                    })}
                                </VStack>
                            </AccordionPanel>
                        </AccordionItem>
                    </Box>

                    {view !== 'board' && (
                    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="visible" mb={3}>
                        <AccordionItem border="none">
                            <AccordionButton px={4} py={3} _hover={{ bg: 'transparent' }}>
                                <Text flex={1} textAlign="left" fontWeight="semibold" fontSize="md" color="gray.800">
                                    Grouping
                                </Text>
                                <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel px={4} pb={4} pt={1}>
                                <VStack spacing={3} align="stretch">
                                    {[
                                        { value: 'none' as DeliveryBoardGrouping, label: 'None', description: 'Show every part as its own row.' },
                                        { value: 'ro' as DeliveryBoardGrouping, label: 'RO', description: 'Group linked parts under the repair order number.' },
                                        { value: 'supplier' as DeliveryBoardGrouping, label: 'Supplier', description: 'Group parts by supplier across every RO.' },
                                    ].map((option) => {
                                        const isSelected = grouping === option.value;
                                        return (
                                            <Box
                                                key={option.value}
                                                as="button"
                                                type="button"
                                                onClick={() => setGrouping(option.value)}
                                                borderWidth="1px"
                                                borderColor={isSelected ? '#0071E3' : 'gray.200'}
                                                borderRadius="lg"
                                                px={4}
                                                py={3}
                                                bg={isSelected ? '#E6F1FD' : 'white'}
                                                textAlign="left"
                                                _hover={{ borderColor: isSelected ? '#0071E3' : 'gray.300', bg: isSelected ? '#E6F1FD' : 'gray.50' }}
                                            >
                                                <Text fontSize="sm" fontWeight="semibold" color={isSelected ? '#0071E3' : 'gray.800'}>
                                                    {option.label}
                                                </Text>
                                                <Text fontSize="xs" color="gray.500" mt={1}>
                                                    {option.description}
                                                </Text>
                                            </Box>
                                        );
                                    })}
                                </VStack>
                            </AccordionPanel>
                        </AccordionItem>
                    </Box>
                    )}

                    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="visible">
                        <AccordionItem border="none">
                            <AccordionButton px={4} py={3} _hover={{ bg: 'transparent' }}>
                                <Text flex={1} textAlign="left" fontWeight="semibold" fontSize="md" color="gray.800">
                                    Ordering
                                </Text>
                                <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel px={4} pb={4} pt={1}>
                                <VStack spacing={3} align="stretch">
                            {[
                                { key: 'orderStartTime' as DeliveryBoardSortKey, label: 'Order Start Time' },
                                { key: 'orderPlacementTime' as DeliveryBoardSortKey, label: 'Order Placement Time' },
                                { key: 'eta' as DeliveryBoardSortKey, label: 'ETAs' },
                            ].map((option) => {
                                const isSelected = sort.key === option.key;
                                return (
                                    <Box
                                        key={option.key}
                                        borderWidth="1px"
                                        borderColor={isSelected ? '#0071E3' : 'gray.200'}
                                        borderRadius="lg"
                                        px={4}
                                        py={3}
                                    >
                                        <HStack justify="space-between">
                                            <Text fontSize="sm" fontWeight="semibold" color={isSelected ? '#0071E3' : 'gray.800'}>
                                                {option.label}
                                            </Text>
                                            <HStack spacing={2}>
                                                <IconButton
                                                    aria-label={`${option.label} ascending`}
                                                    size="xs"
                                                    icon={<Icon as={TbArrowUp as React.ElementType} boxSize={3} />}
                                                    variant={isSelected && sort.dir === 'asc' ? 'solid' : 'outline'}
                                                    colorScheme={isSelected && sort.dir === 'asc' ? 'blue' : undefined}
                                                    onClick={() => updateSortPreference(option.key, 'asc')}
                                                />
                                                <IconButton
                                                    aria-label={`${option.label} descending`}
                                                    size="xs"
                                                    icon={<Icon as={TbArrowDown as React.ElementType} boxSize={3} />}
                                                    variant={isSelected && sort.dir === 'desc' ? 'solid' : 'outline'}
                                                    colorScheme={isSelected && sort.dir === 'desc' ? 'blue' : undefined}
                                                    onClick={() => updateSortPreference(option.key, 'desc')}
                                                />
                                            </HStack>
                                        </HStack>
                                    </Box>
                                );
                            })}
                                </VStack>
                            </AccordionPanel>
                        </AccordionItem>
                    </Box>
                </Accordion>
            ),
        },
    ], [grouping, setGrouping, sort, updateSortPreference, view, setView]);

    const renderRowCheckbox = (row: DeliveryBoardRow, rowIndex: number) => {
        if (!orgCanReceive) return null;
        // The audit tab is read-only and every row there is received; its undo
        // lives in the Received column (onUndoFromAudit), which refetches so
        // the row actually leaves the trail. The optimistic checkbox's undo
        // (onUndoReceive) doesn't refetch and would leave the row listed.
        if (isAuditTab) return null;
        const isSelected = selectedIds.has(row._id);
        const isReceived = receivedIds.has(row._id);
        // Just-received rows keep their (checked) checkbox so receive can be
        // undone; everything else renders the empty gutter when not receivable.
        if (!isReceived && !isDeliveryRowReceivable(session, row)) return null;
        return (
            <Box
                className="delivery-row-checkbox"
                position="absolute"
                left="14px"
                top="50%"
                transform="translateY(-50%)"
                opacity={isSelected || isReceived ? 1 : 0}
                pointerEvents={isSelected || isReceived ? 'auto' : 'none'}
                transition="opacity 150ms ease"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
            >
                <Checkbox
                    aria-label={isReceived ? 'Undo received' : 'Select part'}
                    isChecked={isSelected || isReceived}
                    size="sm"
                    sx={receiveCheckboxSx}
                    onMouseDown={(e) => {
                        e.stopPropagation();
                        shiftKeyRef.current = e.shiftKey;
                    }}
                    onChange={() => (
                        isReceived
                            ? onUndoReceive(row)
                            : toggleSelected(row, rowIndex, shiftKeyRef.current, listRange)
                    )}
                />
            </Box>
        );
    };

    return (
        <Box
            w="calc(100% + 2rem)"
            mx={-4}
            mt={-4}
            mb={-2}
            pb={0}
            h="calc(100dvh - var(--partbay-topbar))"
            display="flex"
            flexDirection="column"
            overflow="hidden"
            position="relative"
        >
            {/* Row 1 — status tabs (list view) + toolbar actions, hairline underneath (ENG-2297). */}
            <ListTabRow
                actions={
                    <HStack spacing={1} flexShrink={0}>
                        <ReportingTableSettingsDrawer
                            settings={settings}
                            onUpdateSettings={updateSettings}
                            hasCustomColumnWidths={Object.keys(colWidths).length > 0}
                            onResetColumnWidths={resetColWidths}
                            columns={{
                                rows: columnRows,
                                onToggle: (key) => toggleColumn(key as DeliveryBoardColumnKey),
                                onReorderDrag: reorderColumns,
                                onResetColumns: resetColumns,
                            }}
                            extraTabs={settingsExtraTabs}
                            hideDisplayTab={view === 'board'}
                            applyImmediately
                        />
                        <RefreshButton
                            refetch={refreshAll}
                            isLoading={isAnyRefetching}
                        />
                    </HStack>
                }
            >
                    {view === 'list' && FILTER_OPTIONS.map((filter) => {
                        const isSelected = selectedFilter === filter.value;
                        return (
                            <Button
                                key={filter.value}
                                size="sm"
                                h="32px"
                                px={4}
                                borderRadius="full"
                                fontWeight="medium"
                                fontSize="sm"
                                flexShrink={0}
                                bg={isSelected ? '#0071E3' : 'transparent'}
                                color={isSelected ? 'white' : 'gray.600'}
                                _hover={{ bg: isSelected ? '#0071E3' : 'gray.100' }}
                                _active={{ bg: isSelected ? '#0071E3' : 'gray.100' }}
                                onClick={() => {
                                    setSelectedFilter(filter.value);
                                    setCurrentPage(1);
                                    clearSelection();
                                }}
                                leftIcon={<Icon as={filter.icon} boxSize={3.5} />}
                            >
                                {filter.label}
                            </Button>
                        );
                    })}
            </ListTabRow>

            {/* Row 2 — search + filters left, location far right (ENG-2297 #4). */}
            <Flex
                ref={toolbarRef}
                my={3}
                px={6}
                flexShrink={0}
                align="center"
                gap={2}
            >
                <CollapsibleSearch
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search by VIN or RO#..."
                    isCompact={searchCompact}
                    width={LIST_PAGE_SEARCH_WIDTH}
                />
                {/* ETA windows don't apply to the received audit trail — it's a receivedAt timeline. */}
                {!(view === 'list' && isAuditTab) && (
                    <DeliveryBoardEtaFilter
                        filters={etaFilters.length > 0 ? etaFilters : DEFAULT_DELIVERY_BOARD_ETA_FILTERS}
                        onChange={(next) => setEtaFilters(next.length > 0 ? next : DEFAULT_DELIVERY_BOARD_ETA_FILTERS)}
                    />
                )}
                {showLocationSelector && (
                    <Box ml="auto" flexShrink={0}>
                        <LocationSelector
                            storageKey="delivery_board_location_ids"
                            onLocationChange={handleLocationChange}
                            width="260px"
                            icon={TbBuilding}
                            iconOnly={locationCompact}
                        />
                    </Box>
                )}
            </Flex>

            <Box
                flex={1}
                display="flex"
                flexDirection="column"
                overflow="hidden"
                minH={0}
            >
                {view === 'board' ? (
                    <Box position="relative" flex={1} minH={0} display="flex" flexDirection="column">
                        <DeliveryBoardKanban
                            columns={boardData.columns}
                            visibleColumnKeys={visibleColumnKeys}
                            selectedIds={selectedIds}
                            onToggleSelect={toggleSelected}
                            receivedIds={receivedIds}
                            onUndoReceive={onUndoReceive}
                        />
                        {orgCanReceive && selectedIds.size > 0 && (
                            <BulkActionBar
                                selectedCount={selectedIds.size}
                                actions={MARK_RECEIVED_ACTIONS}
                                onClearSelection={clearSelection}
                                onAction={openMarkReceivedModal}
                            />
                        )}
                    </Box>
                ) : (
                    <>
                        <Box position="relative" flex={1} minH={0} display="flex" flexDirection="column">
                            <Box
                                ref={scrollContainerRef}
                                flex={1}
                                minH={0}
                                overflow="auto"
                                borderTop="1px solid"
                                borderColor="gray.200"
                                sx={{
                                    ...tableSettingsSx,
                                    overscrollBehavior: 'contain',
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                        height: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: 'transparent',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: '#cbd5e0',
                                        borderRadius: '4px',
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                        background: '#a0aec0',
                                    },
                                    scrollbarWidth: 'thin',
                                    scrollbarColor: '#cbd5e0 transparent',
                                    '& table': {
                                        width: 'max-content !important',
                                        minWidth: '100%',
                                    },
                                    '& thead': {
                                        zIndex: '3 !important',
                                    },
                                    '& th': {
                                        ...(tableSettingsSx['& th'] || {}),
                                        borderBottomWidth: '0 !important',
                                        boxShadow: 'inset 0 -1px 0 0 var(--chakra-colors-gray-200)',
                                        bg: 'gray.50',
                                    },
                                    '& th:first-of-type': {
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 5,
                                        bg: 'gray.50',
                                    },
                                    '& td:first-of-type': {
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 2,
                                        background: 'inherit',
                                    },
                                    // Reveal receive checkboxes on row hover.
                                    '& tr:hover .delivery-row-checkbox': {
                                        opacity: '1 !important',
                                        pointerEvents: 'auto !important',
                                    },
                                }}
                            >
                                {isLoading ? (
                                    <Flex justify="center" align="center" h="400px">
                                        <Spinner size="xl" color="blue.500" />
                                    </Flex>
                                ) : isError ? (
                                    <Box p={8}>
                                        <Alert status="error">
                                            <AlertIcon />
                                            Failed to load the delivery board.
                                        </Alert>
                                    </Box>
                                ) : (
                                    <Table
                                        variant="simple"
                                        size="sm"
                                        minW="100%"
                                        sx={Object.keys(colWidths).length > 0
                                            ? {
                                                tableLayout: 'fixed',
                                                '& td': {
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                },
                                                '& td:last-child': {
                                                    overflow: 'visible',
                                                    textOverflow: 'unset',
                                                    whiteSpace: 'nowrap',
                                                },
                                            }
                                            : { '& td:last-child': { whiteSpace: 'nowrap' } }}
                                    >
                                        <Thead position="sticky" top={0} zIndex={1}>
                                            <Tr bg="gray.50">
                                                {columnDefinitions.map((column, index) => (
                                                    <ResizableTh
                                                        key={column.key}
                                                        columnKey={column.key}
                                                        storedWidth={colWidths[column.key]}
                                                        onResize={updateColWidth}
                                                        minWidth={column.minWidth}
                                                        color="gray.500"
                                                        fontSize="11px"
                                                        fontWeight="semibold"
                                                        letterSpacing="wider"
                                                        py={2.5}
                                                        pl={index === 0 ? FIRST_COL_PL : 3}
                                                        pr={index === columnDefinitions.length - 1 ? 6 : 3}
                                                        textTransform="uppercase"
                                                        borderBottomWidth="1px"
                                                        borderColor="gray.200"
                                                        isNumeric={column.isNumeric}
                                                    >
                                                        {column.header}
                                                    </ResizableTh>
                                                ))}
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {displayRows.map((entry) => {
                                                if (entry.type === 'group') {
                                                    const receivableRows = entry.rows.filter(isReceivable);
                                                    const allGroupSelected = receivableRows.length > 0
                                                        && receivableRows.every((row) => selectedIds.has(row._id));
                                                    const someGroupSelected = receivableRows.some((row) => selectedIds.has(row._id));
                                                    return (
                                                        <Tr key={`group-${entry.key}`} bg="gray.50">
                                                            <Td
                                                                colSpan={columnDefinitions.length}
                                                                py={CELL_PY}
                                                                pl={FIRST_COL_PL}
                                                                pr={6}
                                                                borderBottomWidth="1px"
                                                                borderColor="gray.200"
                                                                position="relative"
                                                            >
                                                                {receivableRows.length > 0 && (
                                                                    <Box
                                                                        className="delivery-row-checkbox"
                                                                        position="absolute"
                                                                        left="14px"
                                                                        top="50%"
                                                                        transform="translateY(-50%)"
                                                                        opacity={someGroupSelected ? 1 : 0}
                                                                        pointerEvents={someGroupSelected ? 'auto' : 'none'}
                                                                        transition="opacity 150ms ease"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        <Checkbox
                                                                            aria-label={`Select all parts in ${entry.label}`}
                                                                            isChecked={allGroupSelected}
                                                                            isIndeterminate={someGroupSelected && !allGroupSelected}
                                                                            size="sm"
                                                                            sx={receiveCheckboxSx}
                                                                            onChange={() => toggleGroupSelected(entry.rows)}
                                                                        />
                                                                    </Box>
                                                                )}
                                                                <HStack justify="space-between">
                                                                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                                        {entry.label}
                                                                    </Text>
                                                                    <Text fontSize="xs" color="gray.500">
                                                                        {entry.count} parts
                                                                    </Text>
                                                                </HStack>
                                                            </Td>
                                                        </Tr>
                                                    );
                                                }

                                                const row = entry.row;
                                                const isReceived = receivedIds.has(row._id);
                                                // Only rows that can actually be received are clickable —
                                                // unfulfilled/audit rows and orders predating the receive
                                                // opt-in get no pointer cursor or hover-select affordance.
                                                const isSelectable = isReceivable(row);
                                                const rowIndex = entry.index;
                                                return (
                                                    <Tr
                                                        key={row._id}
                                                        bg={selectedIds.has(row._id) ? 'blue.50' : 'white'}
                                                        opacity={isReceived ? 0.7 : 1}
                                                        _hover={isSelectable
                                                            ? { bg: selectedIds.has(row._id) ? 'blue.100' : 'gray.50', cursor: 'pointer' }
                                                            : undefined}
                                                        onClick={(e) => {
                                                            if (!isSelectable) return;
                                                            toggleSelected(row, rowIndex, e.shiftKey, listRange);
                                                        }}
                                                    >
                                                        {columnDefinitions.map((column, index) => (
                                                            <Td
                                                                key={column.key}
                                                                py={CELL_PY}
                                                                pl={index === 0 ? FIRST_COL_PL : 3}
                                                                pr={index === columnDefinitions.length - 1 ? 6 : 3}
                                                                borderBottomWidth="1px"
                                                                borderColor="gray.100"
                                                                isNumeric={column.isNumeric}
                                                                position={index === 0 ? 'relative' : undefined}
                                                            >
                                                                {index === 0 && renderRowCheckbox(row, rowIndex)}
                                                                {column.render(row)}
                                                            </Td>
                                                        ))}
                                                    </Tr>
                                                );
                                            })}
                                        </Tbody>
                                    </Table>
                                )}
                            </Box>
                            {showRightShadow && (
                                <Box
                                    position="absolute"
                                    right={0}
                                    top={0}
                                    bottom={0}
                                    w="48px"
                                    bgGradient="linear(to-r, rgba(255,255,255,0), rgba(255,255,255,0.85))"
                                    pointerEvents="none"
                                    zIndex={4}
                                />
                            )}
                            {orgCanReceive && selectedIds.size > 0 && (
                                <BulkActionBar
                                    selectedCount={selectedIds.size}
                                    actions={MARK_RECEIVED_ACTIONS}
                                    onClearSelection={clearSelection}
                                    onAction={openMarkReceivedModal}
                                />
                            )}
                        </Box>
                        <Box flexShrink={0} borderTop="1px solid" borderColor="gray.200">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                                isLoading={isLoading || isRefetching}
                                hasNextPage={rows.hasNextPage}
                            />
                        </Box>
                    </>
                )}
            </Box>

            <MarkReceivedModal
                isOpen={isMarkReceivedOpen}
                onClose={() => setIsMarkReceivedOpen(false)}
                orders={markReceivedOrders}
                onSuccess={onMarkReceivedSuccess}
            />

            <OrderDrawer
                isOpen={drawerTarget !== null}
                onClose={() => setDrawerTarget(null)}
                orderId={drawerTarget?.orderId ?? null}
                initialPanelState={{
                    panel: 'none',
                    supplierInitialTab: 'quoting-comments',
                    supplierInitialSupplierOrderId: null,
                    workflowTab: 'ordered',
                }}
                clickedSupplierOrderId={drawerTarget?.supplierOrderId ?? undefined}
                clickedOrderPartId={drawerTarget?.orderPartId ?? undefined}
            />
        </Box>
    );
};

export default DeliveryBoard;
